import type { VariantProps } from 'class-variance-authority'

import { Button as ButtonPrimitive } from '@base-ui/react/button'
import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils'

/*
 * GoodUnited / Button. Figma: 10 / Components / Button.
 * variant: default (action/primary), outline (action/secondary), ghost, link, destructive.
 * size: sm 32, default 40, lg 44, xl 48 (the original GoodUnited size), icon 40, icon-sm 32.
 * align: center or start (ghost buttons in lists and menus).
 */
const buttonVariants = cva(
    'group/button inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-md border border-transparent font-medium whitespace-nowrap transition-colors select-none outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus disabled:pointer-events-none data-disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=size-])]:size-4',
    {
        variants: {
            variant: {
                default: 'bg-action-primary text-action-primary-foreground hover:bg-action-primary-hover active:bg-action-primary-pressed disabled:bg-action-disabled disabled:text-action-disabled-foreground data-disabled:bg-action-disabled data-disabled:text-action-disabled-foreground',
                outline: 'border-action-secondary-border bg-action-secondary text-action-secondary-foreground hover:bg-action-secondary-hover active:bg-action-secondary-pressed aria-expanded:bg-action-secondary-hover disabled:bg-action-disabled disabled:text-action-disabled-foreground data-disabled:bg-action-disabled data-disabled:text-action-disabled-foreground',
                ghost: 'bg-transparent text-text-primary hover:bg-action-secondary-hover active:bg-action-secondary-pressed aria-expanded:bg-action-secondary-hover disabled:text-text-disabled data-disabled:text-text-disabled',
                link: 'bg-transparent px-0.5 text-text-link hover:text-text-link-hover disabled:text-text-disabled data-disabled:text-text-disabled',
                destructive: 'bg-action-destructive text-action-destructive-foreground hover:bg-action-destructive-hover active:bg-action-destructive-pressed disabled:bg-action-disabled disabled:text-action-disabled-foreground data-disabled:bg-action-disabled data-disabled:text-action-disabled-foreground'
            },
            size: {
                sm: 'h-8 px-3 text-sm',
                default: 'h-10 px-4 text-base',
                lg: 'h-11 px-5 text-base',
                xl: 'h-12 px-6 text-base',
                icon: 'size-10 justify-center p-0',
                'icon-sm': 'size-8 justify-center p-0'
            },
            align: {
                center: 'justify-center',
                start: 'justify-start text-left'
            }
        },
        compoundVariants: [
            { variant: 'link', size: ['sm', 'default', 'lg', 'xl'], className: 'px-0.5' }
        ],
        defaultVariants: {
            variant: 'default',
            size: 'default',
            align: 'center'
        }
    }
)

type ButtonProps = Omit<ButtonPrimitive.Props, 'className'> & VariantProps<typeof buttonVariants> & {
    className?: string
}

function Button({
    className,
    variant = 'default',
    size = 'default',
    align = 'center',
    ...props
}: ButtonProps) {
    return (
        <ButtonPrimitive
            data-slot='button'
            data-variant={variant}
            data-size={size}
            className={cn(buttonVariants({ variant, size, align }), className)}
            {...props}
        />
    )
}

export { Button, buttonVariants }
export type { ButtonProps }
